import { useEffect, useMemo, useRef, useState, FormEvent } from 'react';
import { Check, ChevronDown, Loader2 } from 'lucide-react';
import { Modal, Button } from '@ai-tutor/ui';
import { TagInput } from './TagInput';
import { cn } from '@/lib/utils';
import type { VersionSummary } from '@/lib/api-client';

/**
 * Edit a lesson's metadata - title, description (overview), and tags -
 * in one modal with a single Save button.
 *
 * Per-version editing: if a `versions` list is passed, a dropdown lets the
 * user pick which version's snapshot to edit. The form reseeds with that
 * version's title/description/tags on selection. The submit handler
 * receives `version` in the payload so the caller can pass it through to
 * `PATCH /lessons/{id}` - the backend writes the snapshot, and mirrors it
 * to the UserLesson row only when the chosen version is current.
 *
 * Saves through the unified `PATCH /lessons/{id}` endpoint, which is
 * a pure metadata edit (no version bump, no regeneration). For
 * regenerating content from a new prompt use the prompt-edit flow on
 * the lesson page instead.
 */

export interface EditLessonDetailsPayload {
  title: string;
  description: string;
  tags: string[];
  /** Set when the user picked a specific version in the modal. */
  version?: string;
}

export interface EditLessonDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialTitle: string;
  initialDescription: string;
  initialTags: string[];
  onSubmit: (payload: EditLessonDetailsPayload) => Promise<void> | void;
  /**
   * Optional version list. When provided, the modal renders a version
   * dropdown above the title input and lets the user retarget the edit
   * at a specific version's snapshot. Pass the result of
   * `useVersionList(lessonId).data?.versions`. If omitted, the modal
   * behaves as a single-target editor that writes to the lesson row.
   */
  versions?: VersionSummary[];
}

export function EditLessonDetailsModal({
  isOpen,
  onClose,
  initialTitle,
  initialDescription,
  initialTags,
  onSubmit,
  versions,
}: EditLessonDetailsModalProps) {
  // Default-target the current version when versions are provided; falls
  // back to the most recent version (highest semver) if none flagged
  // is_current (edge case for partially-migrated data).
  const defaultVersion = useMemo(() => {
    if (!versions || versions.length === 0) return undefined;
    const current = versions.find(v => v.is_current);
    if (current) return current.version;
    const sorted = [...versions].sort(
      (a, b) => parseFloat(b.version) - parseFloat(a.version)
    );
    return sorted[0]?.version;
  }, [versions]);

  const [selectedVersion, setSelectedVersion] = useState<string | undefined>(
    defaultVersion
  );
  const [title, setTitle] = useState(initialTitle);
  const [description, setDescription] = useState(initialDescription);
  const [tags, setTags] = useState<string[]>(initialTags);
  const [submitting, setSubmitting] = useState(false);
  const [versionMenuOpen, setVersionMenuOpen] = useState(false);
  const versionMenuRef = useRef<HTMLDivElement>(null);

  // Close the version dropdown on outside-click or Escape so it behaves like
  // the FlowSelector + VersionSelector popovers elsewhere in the app.
  useEffect(() => {
    if (!versionMenuOpen) return;
    const onClick = (e: MouseEvent) => {
      if (
        versionMenuRef.current &&
        !versionMenuRef.current.contains(e.target as Node)
      ) {
        setVersionMenuOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setVersionMenuOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [versionMenuOpen]);

  // Pre-sort once for both the trigger label and the popover list.
  const sortedVersions = useMemo(() => {
    if (!versions) return [];
    return [...versions].sort(
      (a, b) => parseFloat(b.version) - parseFloat(a.version)
    );
  }, [versions]);

  // When the modal opens, reseed everything from the current version's
  // snapshot (if versions are provided) or from the initial* props (legacy).
  useEffect(() => {
    if (!isOpen) return;
    setSelectedVersion(defaultVersion);
    if (versions && defaultVersion) {
      const v = versions.find(x => x.version === defaultVersion);
      setTitle(v?.title ?? initialTitle);
      setDescription(v?.description ?? initialDescription);
      setTags(v?.tags?.length ? v.tags : initialTags);
    } else {
      setTitle(initialTitle);
      setDescription(initialDescription);
      setTags(initialTags);
    }
    setSubmitting(false);
  }, [
    isOpen,
    defaultVersion,
    versions,
    initialTitle,
    initialDescription,
    initialTags,
  ]);

  // Switching the dropdown reseeds the form with that version's snapshot.
  const handleVersionChange = (v: string) => {
    setSelectedVersion(v);
    setVersionMenuOpen(false);
    const target = versions?.find(x => x.version === v);
    setTitle(target?.title ?? '');
    setDescription(target?.description ?? '');
    setTags(target?.tags ?? []);
  };

  const trimmedTitle = title.trim();
  const titleEmpty = !trimmedTitle;

  // Baseline for "dirty" detection: when a version is selected, compare
  // against that version's snapshot; otherwise against the initial* props.
  const baseline = useMemo(() => {
    if (versions && selectedVersion) {
      const v = versions.find(x => x.version === selectedVersion);
      return {
        title: v?.title ?? initialTitle,
        description: v?.description ?? initialDescription,
        tags: v?.tags ?? initialTags,
      };
    }
    return {
      title: initialTitle,
      description: initialDescription,
      tags: initialTags,
    };
  }, [
    versions,
    selectedVersion,
    initialTitle,
    initialDescription,
    initialTags,
  ]);

  const dirty =
    trimmedTitle !== baseline.title.trim() ||
    description.trim() !== baseline.description.trim() ||
    !sameTags(tags, baseline.tags);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (titleEmpty || !dirty) return;
    setSubmitting(true);
    try {
      await onSubmit({
        title: trimmedTitle,
        description: description.trim(),
        tags,
        version: selectedVersion,
      });
      onClose();
    } finally {
      setSubmitting(false);
    }
  };

  const isVersionedMode = Boolean(versions && versions.length > 0);

  return (
    <Modal isOpen={isOpen} onClose={onClose} title='Edit lesson' size='lg'>
      <form onSubmit={handleSubmit} className='space-y-5 px-6 py-5'>
        <p className='text-xs text-muted-foreground'>
          Tweak the title, overview, or tags. These are quick edits and won't
          regenerate your lesson. To change what gets generated, edit the prompt
          from the lesson page.
        </p>

        {/* VERSION SELECTOR -------------------------------------------- */}
        {/* Custom dropdown - mirrors the FlowSelector / VersionSelector
            pattern (rounded-xl items, indigo wash on selected, indigo
            "current" pill). Replaces the native <select> which couldn't
            be themed to match those components. */}
        {isVersionedMode && (
          <div className='space-y-1.5' ref={versionMenuRef}>
            <label
              htmlFor='lesson-version-trigger'
              className='text-sm font-medium text-foreground/80'
            >
              Editing version
            </label>
            <div className='relative'>
              <button
                id='lesson-version-trigger'
                type='button'
                onClick={() => setVersionMenuOpen(o => !o)}
                aria-haspopup='listbox'
                aria-expanded={versionMenuOpen}
                className={cn(
                  'flex w-full items-center justify-between gap-2 rounded-md border border-input bg-card px-3 py-2 text-sm transition-colors max-sm:min-h-11',
                  'hover:border-brand-300 dark:hover:border-primary/60',
                  '!outline-none focus:border-brand-400 focus:!outline-none focus:!ring-0 focus:!ring-offset-0 dark:focus:border-primary',
                  'focus-visible:!outline-none focus-visible:!ring-0 focus-visible:!ring-offset-0',
                  versionMenuOpen && 'border-brand-400 dark:border-primary'
                )}
              >
                <span className='flex items-baseline gap-2'>
                  <span className='font-medium text-foreground'>
                    v{selectedVersion ?? ''}
                  </span>
                  {sortedVersions.find(v => v.version === selectedVersion)
                    ?.is_current && (
                    <span className='dark:tint-brand rounded-full bg-brand-100 px-2 py-0.5 text-xs font-medium text-brand-700'>
                      Current
                    </span>
                  )}
                </span>
                <ChevronDown
                  className={cn(
                    'h-3.5 w-3.5 text-muted-foreground transition-transform duration-200',
                    versionMenuOpen && 'rotate-180'
                  )}
                />
              </button>

              {versionMenuOpen && (
                <div
                  role='listbox'
                  tabIndex={-1}
                  className='absolute left-0 right-0 top-full z-50 mt-2 max-h-72 overflow-y-auto rounded-2xl border border-border bg-card p-1.5 shadow-lg'
                >
                  {sortedVersions.map(v => {
                    const isSelected = v.version === selectedVersion;
                    return (
                      <button
                        key={v.version}
                        type='button'
                        role='option'
                        aria-selected={isSelected}
                        onClick={() => handleVersionChange(v.version)}
                        className={cn(
                          'flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left transition-colors',
                          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-400/60 focus-visible:ring-offset-0 dark:focus-visible:ring-primary/60',
                          isSelected
                            ? 'bg-brand-50/70 dark:bg-tint-brand'
                            : 'hover:bg-muted dark:hover:bg-accent'
                        )}
                      >
                        <div className='min-w-0 flex-1'>
                          <div className='flex items-center gap-2'>
                            <span
                              className={cn(
                                'text-sm font-medium',
                                isSelected
                                  ? 'text-brand-700 dark:text-tint-brand-foreground'
                                  : 'text-foreground'
                              )}
                            >
                              v{v.version}
                            </span>
                            {v.is_current && (
                              <span className='dark:tint-brand rounded-full bg-brand-100 px-2 py-0.5 text-xs font-medium text-brand-700'>
                                Current
                              </span>
                            )}
                          </div>
                          {v.title && (
                            <div className='mt-0.5 truncate text-xs text-muted-foreground'>
                              {v.title}
                            </div>
                          )}
                        </div>
                        {isSelected && (
                          <Check className='h-4 w-4 flex-shrink-0 text-brand-600 dark:text-primary' />
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
            <p className='text-xs text-muted-foreground'>
              Edits only affect this version's snapshot. Changes to the current
              version also update the lesson card.
            </p>
          </div>
        )}

        {/* TITLE -------------------------------------------------------- */}
        <div className='space-y-1.5'>
          <label
            htmlFor='lesson-title'
            className='text-sm font-medium text-foreground/80'
          >
            Title
          </label>
          <input
            id='lesson-title'
            type='text'
            value={title}
            onChange={e => setTitle(e.target.value)}
            maxLength={120}
            // eslint-disable-next-line jsx-a11y/no-autofocus -- modal entry input
            autoFocus
            className={cn(
              // 44px and 16px type on a phone (iOS zooms into smaller
              // fields).
              'w-full rounded-md border border-input bg-card px-3 py-2 text-base max-sm:h-11 sm:text-sm',
              // `globals.css` applies `ring-2 ring-ring ring-offset-2` to
              // every focus-visible input via a base-layer rule. Need
              // `!important` to beat it; otherwise clicking the input
              // shows a 2px halo on top of the existing border.
              '!outline-none focus:border-brand-400 focus:!outline-none focus:!ring-0 focus:!ring-offset-0 dark:focus:border-primary',
              'focus-visible:!outline-none focus-visible:!ring-0 focus-visible:!ring-offset-0',
              'placeholder:text-muted-foreground/60'
            )}
            placeholder='Lesson title'
          />
          {titleEmpty && (
            <p className='text-xs text-amber-600 dark:text-amber-400'>
              Title cannot be empty.
            </p>
          )}
        </div>

        {/* DESCRIPTION (Overview) ------------------------------------- */}
        <div className='space-y-1.5'>
          <label
            htmlFor='lesson-description'
            className='text-sm font-medium text-foreground/80'
          >
            Overview
          </label>
          <textarea
            id='lesson-description'
            value={description}
            onChange={e => setDescription(e.target.value)}
            rows={3}
            maxLength={600}
            className={cn(
              'w-full resize-none rounded-md border border-input bg-card px-3 py-2 text-base leading-relaxed sm:text-sm',
              // Same `!important` override as the title - the global
              // base-layer rule in globals.css applies a 2px ring to
              // every focused textarea otherwise.
              '!outline-none focus:border-brand-400 focus:!outline-none focus:!ring-0 focus:!ring-offset-0 dark:focus:border-primary',
              'focus-visible:!outline-none focus-visible:!ring-0 focus-visible:!ring-offset-0',
              'placeholder:text-muted-foreground/60'
            )}
            placeholder='A short description of what this lesson covers…'
          />
          <p className='text-right text-xs text-muted-foreground'>
            {description.length}/600
          </p>
        </div>

        {/* TAGS --------------------------------------------------------- */}
        <div className='space-y-1.5'>
          <label className='text-sm font-medium text-foreground/80'>Tags</label>
          <TagInput value={tags} onChange={setTags} />
        </div>

        {/* ACTIONS ------------------------------------------------------ */}
        <div className='flex justify-end gap-2 pt-1 max-sm:[&>button]:h-11 max-sm:[&>button]:flex-1'>
          <Button type='button' variant='ghost' onClick={onClose}>
            Cancel
          </Button>
          <Button type='submit' disabled={submitting || titleEmpty || !dirty}>
            {submitting ? (
              <Loader2 className='h-3.5 w-3.5 animate-spin' />
            ) : null}
            Save changes
          </Button>
        </div>
      </form>
    </Modal>
  );
}

function sameTags(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false;
  // Tags are unordered conceptually, but order changes also don't matter
  // for the API - sort copies before comparison to avoid false-dirty.
  const sa = [...a].map(t => t.trim()).sort();
  const sb = [...b].map(t => t.trim()).sort();
  return sa.every((t, i) => t === sb[i]);
}
